import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $createLineBreakNode, $createParagraphNode, $createTextNode, $getRoot, $insertNodes, createEditor, type LexicalEditor } from 'lexical';
import { stripHtmlAttributes } from './stripHtmlAttributes';
import { COMPOSER_NAMESPACE, COMPOSER_NODES, composerTheme } from './config';

const rethrow = (e: Error): never => {
  throw e;
};

// Line breaks, not one text run: a single node's newlines vanish from the HTML part once its pre-wrap style is stripped.
export function seedFrom(text: string) {
  return () => {
    const root = $getRoot();
    const p = $createParagraphNode();
    text.split('\n').forEach((line, i) => {
      if (i > 0) p.append($createLineBreakNode());
      if (line) p.append($createTextNode(line));
    });
    root.append(p);
  };
}

/** Reads HTML into the editor's own nodes, replacing what the root holds. */
export function $seedFromHtml(editor: LexicalEditor, html: string): void {
  const nodes = $generateNodesFromDOM(editor, new DOMParser().parseFromString(html, 'text/html'));
  $getRoot().select();
  $insertNodes(nodes);
}

function withComposerEditor<T>(fill: (editor: LexicalEditor) => void, read: (editor: LexicalEditor) => T): T {
  const editor = createEditor({ namespace: COMPOSER_NAMESPACE, nodes: [...COMPOSER_NODES], theme: composerTheme, onError: rethrow });
  editor.update(() => fill(editor), { discrete: true });
  return editor.getEditorState().read(() => read(editor));
}

/** The HTML the composer sends for plain text it was seeded with and nobody touched. */
export function composerHtmlFromText(text: string): string {
  if (text.trim() === '') return '';
  return withComposerEditor(seedFrom(text), (editor) => stripHtmlAttributes($generateHtmlFromNodes(editor, null)));
}

/** The plain twin the composer derives from HTML once it is loaded into the editor. */
export function composerTextFromHtml(html: string): string {
  return withComposerEditor(
    (editor) => $seedFromHtml(editor, html),
    () => $getRoot().getTextContent(),
  );
}
